import { useEffect, useMemo, useState } from 'react';

export interface Pagination<T> {
  pageItems: T[];
  page: number;
  pageCount: number;
  total: number;
  /** 1-based index of the first item on this page (0 when empty). */
  firstIndex: number;
  lastIndex: number;
  setPage: (page: number) => void;
}

/**
 * Client-side paging over an in-memory list. Jumps back to page 1 whenever
 * `resetKey` changes (a new category, sort or filter) so a short list is
 * never left showing an empty page.
 */
export function usePagination<T>(items: T[], pageSize: number, resetKey: unknown = null): Pagination<T> {
  const [page, setPage] = useState(1);
  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    setPage(1);
  }, [resetKey]);

  // Deleting the last item on the last page must not strand the view.
  const currentPage = Math.min(page, pageCount);

  const pageItems = useMemo(
    () => items.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [items, currentPage, pageSize]
  );

  return {
    pageItems,
    page: currentPage,
    pageCount,
    total,
    firstIndex: total === 0 ? 0 : (currentPage - 1) * pageSize + 1,
    lastIndex: Math.min(currentPage * pageSize, total),
    setPage
  };
}
